import { Link, router, usePage } from '@inertiajs/react';
import {
    BarChart3,
    Calendar,
    ChevronRight,
    ClipboardCheck,
    FileText,
    Home,
    LayoutGrid,
    LogOut,
    Settings,
    UserPlus,
    Users,
} from 'lucide-react';
import AppLogoIcon from '@/components/app-logo-icon';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    useSidebar,
} from '@/components/ui/sidebar';
import { useCurrentUrl } from '@/hooks/use-current-url';
import { useInitials } from '@/hooks/use-initials';
import { logout } from '@/routes';
import { dashboard } from '@/routes';
import { cn } from '@/lib/utils';
import type { NavItem } from '@/types';

export const mainNavItems: NavItem[] = [
    { title: 'Dashboard', href: '/admin/dashboard', icon: LayoutGrid },
    { title: 'Kelola Santri', href: '/admin/santri', icon: Users },
    { title: 'Pendaftaran Baru', href: '/admin/users/pending', icon: UserPlus },
    { title: 'Kelola Izin', href: '/admin/izin', icon: ClipboardCheck },
    { title: 'Kepulangan', href: '/admin/kepulangan', icon: Home },
    { title: 'Kalender Akademik', href: '/admin/kalender', icon: Calendar },
    { title: 'Laporan', href: '/admin/laporan', icon: BarChart3 },
    { title: 'Pengaturan', href: '/admin/pengaturan', icon: Settings },
    { title: 'Log Aktivitas', href: '/admin/log-aktivitas', icon: FileText },
];

const navGroups = [
    {
        label: 'Overview',
        items: ['Dashboard'],
    },
    {
        label: 'Manajemen',
        items: ['Kelola Santri', 'Pendaftaran Baru', 'Kelola Izin', 'Kepulangan'],
    },
    {
        label: 'Laporan & Sistem',
        items: ['Kalender Akademik', 'Laporan', 'Pengaturan', 'Log Aktivitas'],
    },
];

function NavItemRow({
    item,
    badge,
}: {
    item: NavItem;
    badge?: number;
}) {
    const { isCurrentOrParentUrl } = useCurrentUrl();
    const { state } = useSidebar();
    const active = isCurrentOrParentUrl(item.href);
    const collapsed = state === 'collapsed';

    return (
        <Link
            href={item.href}
            prefetch
            instant
            title={collapsed ? item.title : undefined}
            className={cn(
                'group relative flex h-9 items-center gap-3 rounded-lg px-3 text-sm transition-all duration-150',
                active
                    ? 'bg-white/10 text-white'
                    : 'text-white/45 hover:bg-white/5 hover:text-white/80',
            )}
        >
            {/* Active left bar */}
            {active && (
                <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-admin-gold" />
            )}

            <item.icon
                className={cn('h-4 w-4 shrink-0 transition-colors', active ? 'text-white' : 'text-white/35 group-hover:text-white/60')}
                strokeWidth={active ? 2.2 : 1.8}
            />

            {!collapsed && (
                <>
                    <span className={cn('flex-1 truncate font-medium tracking-tight', active && 'font-semibold')}>
                        {item.title}
                    </span>
                    {badge !== undefined && badge > 0 && (
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-admin-gold px-1.5 text-[10px] font-bold text-admin-deep">
                            {badge}
                        </span>
                    )}
                </>
            )}
        </Link>
    );
}

export function AppSidebar() {
    const { auth } = usePage().props as any;
    const { state } = useSidebar();
    const getInitials = useInitials();
    const collapsed = state === 'collapsed';
    const isSantri = auth.user?.role === 'santri' || !!auth.user?.santri;

    const getBadge = (title: string) => {
        if (title === 'Kelola Izin') return auth.pending_izin_count;
        if (title === 'Kepulangan') return auth.pending_kepulangan_count;
        return undefined;
    };

    return (
        <Sidebar
            collapsible="icon"
            variant="sidebar"
            className="border-r-0"
        >
            <div className="flex h-full flex-col bg-admin-deep dark:bg-[#071210]">

                {/* ── Logo ── */}
                <SidebarHeader className="shrink-0 px-4 py-5">
                    <Link href="/admin/dashboard" prefetch className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-admin-primary/20 text-white ring-1 ring-white/10">
                            <AppLogoIcon className="size-5" />
                        </div>
                        {!collapsed && (
                            <div className="flex flex-col leading-none">
                                <span className="text-[15px] font-bold tracking-tight text-white">MYASWAJA</span>
                                <span className="text-[9px] font-medium tracking-[0.25em] text-white/30 uppercase">Control Panel</span>
                            </div>
                        )}
                    </Link>
                </SidebarHeader>

                {/* ── Nav ── */}
                <SidebarContent className="hide-scrollbar flex-1 overflow-y-auto px-3">
                    {collapsed ? (
                        // Collapsed: just icons
                        <div className="space-y-1 py-2">
                            {mainNavItems.map((item) => (
                                <NavItemRow key={item.title} item={item} badge={getBadge(item.title)} />
                            ))}
                        </div>
                    ) : (
                        // Expanded: grouped with labels
                        <div className="space-y-6 py-2">
                            {navGroups.map((group) => {
                                const groupItems = mainNavItems.filter((i) => group.items.includes(i.title));
                                return (
                                    <div key={group.label}>
                                        <p className="mb-1.5 px-3 text-[9px] font-semibold tracking-[0.2em] text-white/20 uppercase">
                                            {group.label}
                                        </p>
                                        <div className="space-y-0.5">
                                            {groupItems.map((item) => (
                                                <NavItemRow key={item.title} item={item} badge={getBadge(item.title)} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </SidebarContent>

                {/* ── Footer ── */}
                <SidebarFooter className="shrink-0 border-t border-white/[0.06] px-3 py-3">
                    {/* Switch to Santri */}
                    {isSantri && !collapsed && (
                        <Link
                            href={dashboard()}
                            className="mb-2 flex items-center justify-between rounded-lg bg-admin-gold/10 px-3 py-2 text-[11px] font-semibold text-admin-gold transition-colors hover:bg-admin-gold/15"
                        >
                            <span>Santri Dashboard</span>
                            <ChevronRight className="h-3.5 w-3.5" />
                        </Link>
                    )}

                    {/* User card */}
                    <div className="flex items-center gap-3 rounded-lg p-2">
                        <Avatar className="h-8 w-8 shrink-0 rounded-lg">
                            <AvatarImage src={auth.user?.avatar} alt={auth.user?.name} />
                            <AvatarFallback className="rounded-lg bg-admin-primary/30 text-xs font-bold text-white">
                                {getInitials(auth.user?.name || '')}
                            </AvatarFallback>
                        </Avatar>
                        {!collapsed && (
                            <>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-[12px] font-semibold text-white">{auth.user?.name}</p>
                                    <p className="truncate text-[10px] text-white/30">{auth.user?.email}</p>
                                </div>
                                <Link
                                    href={logout()}
                                    method="post"
                                    as="button"
                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white/25 transition-colors hover:bg-white/5 hover:text-white/60"
                                    title="Logout"
                                >
                                    <LogOut className="h-3.5 w-3.5" />
                                </Link>
                            </>
                        )}
                    </div>
                </SidebarFooter>
            </div>
        </Sidebar>
    );
}
